import {
  TextField,
  Input as AriaInput,
  Label,
  FieldError,
  Text,
  type TextFieldProps,
} from 'react-aria-components'
import { forwardRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InputProps extends Omit<TextFieldProps, 'children'> {
  label?: string
  description?: string
  errorMessage?: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  inputClassName?: string
  /**
   * Show a clear button when the input has a value.
   * Defaults to true for text and search inputs.
   * Set to false to disable the clear button.
   */
  clearable?: boolean
  /** Optional callback when clear button is clicked (in addition to clearing the value) */
  onClear?: () => void
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputClassName,
      label,
      description,
      errorMessage,
      placeholder,
      type = 'text',
      leftIcon,
      rightIcon,
      clearable,
      onClear,
      onChange,
      isDisabled,
      isReadOnly,
      isRequired,
      value,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) => {
    // Ensure we always have an accessible label
    const accessibleLabel = label || ariaLabel || placeholder

    // Default clearable to true for text and search inputs
    const isClearable = clearable ?? (type === 'text' || type === 'search')

    // Determine if we should show the clear button
    const hasValue = value !== undefined && value !== ''
    const showClearButton = isClearable && hasValue && !isDisabled && !isReadOnly

    // Handle clear button click - automatically clears and calls optional onClear
    const handleClear = () => {
      // Call onChange with empty string to clear the input
      if (onChange) {
        onChange('')
      }
      // Call optional onClear callback for any additional handling
      if (onClear) {
        onClear()
      }
    }

    // Determine if we have a right element (rightIcon or clear button)
    const hasRightElement = rightIcon || showClearButton

    return (
      <TextField
        className={cn('flex flex-col gap-1.5', className)}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        aria-label={!label ? accessibleLabel : undefined}
        value={value}
        onChange={onChange}
        {...props}
      >
        {label && (
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {label}
            {isRequired && <span className="text-error-500 ml-0.5">*</span>}
          </Label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {leftIcon}
            </div>
          )}
          <AriaInput
            ref={ref}
            type={type}
            placeholder={placeholder}
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-base',
              'placeholder:text-gray-400',
              'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
              'dark:focus:border-brand-400 dark:focus:ring-brand-400',
              leftIcon && 'pl-10',
              hasRightElement && 'pr-10',
              errorMessage && 'border-error-500 focus:border-error-500 focus:ring-error-500',
              inputClassName
            )}
          />
          {/* Clear button takes precedence over rightIcon when shown */}
          {showClearButton ? (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              aria-label="Clear input"
            >
              <X className="h-4 w-4" />
            </button>
          ) : rightIcon ? (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          ) : null}
        </div>
        {description && !errorMessage && (
          <Text slot="description" className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </Text>
        )}
        {errorMessage && (
          <FieldError className="text-sm text-error-600 dark:text-error-400">
            {errorMessage}
          </FieldError>
        )}
      </TextField>
    )
  }
)

Input.displayName = 'Input'

export default Input
